/**
 * Ambient store for the RAW inbound `x-user-context` header value
 * (base64 `UserActorEnvelope`), used to propagate user identity across
 * A→B S2S hops (A2 / D454 / drift 0cc7c7af).
 *
 * The server auth path (`withAuthUnary` / `withAuthUnaryValue`) wraps the
 * handler invocation in `userContextStore.run(rawHeader, () => handler(...))`
 * ONLY when an inbound `x-user-context` header is present. The client auth
 * interceptor (`createClientAuthInterceptor`) reads
 * `userContextStore.getStore()` and re-attaches the RAW bytes unchanged onto
 * the outgoing call — preserving byte-parity + the D454 `on_behalf_of` agent
 * marker (never re-encoded).
 *
 * An `AsyncLocalStorage` (not a module-level mutable) is used because a Node
 * gRPC server serves concurrent calls on the same event loop: the store scopes
 * the raw header to the async context of the in-flight handler, so overlapping
 * requests never leak each other's user context onto outgoing calls.
 *
 * SUBPATH-SPLIT SAFETY (request 4f1facd4 class): `@nodii/grpc-auth` ships
 * SEPARATE tsup subpath bundles (`splitting: false`) — the server path lives in
 * `/server`, the client interceptor in `/client`. A plain module-level
 * `new AsyncLocalStorage()` would therefore be DUPLICATED per bundle: the server
 * would `.run(raw)` on the /server bundle's instance while the client reads a
 * DIFFERENT /client-bundle instance → `getStore()` always `undefined` →
 * forwarding SILENTLY dead (exactly the fence-is-dead failure mode A2 closes).
 * The single instance is therefore held on a `globalThis` slot (same mechanism
 * as the JWKS resolver / permission cache) so every bundle copy shares it.
 */
import { AsyncLocalStorage } from "node:async_hooks";
import { processGlobalSlot } from "./global-state";

const alsSlot = processGlobalSlot<AsyncLocalStorage<string>>("userContextAls");

function resolveStore(): AsyncLocalStorage<string> {
  let als = alsSlot.get();
  if (!als) {
    als = new AsyncLocalStorage<string>();
    alsSlot.set(als);
  }
  return als;
}

/**
 * Process-wide ambient store carrying the RAW inbound `x-user-context`
 * header string for the duration of a handler invocation. `getStore()` is
 * `undefined` (no active store) outside a handler or for an S2S-only call
 * with no inbound user context.
 *
 * Backed by ONE `globalThis`-slotted `AsyncLocalStorage` so the server
 * (`/server`) and client (`/client`) subpath bundles share the same instance
 * within a realm — otherwise the split would silently break forwarding.
 */
export const userContextStore: AsyncLocalStorage<string> = resolveStore();
